import { cn } from "@/lib/cn";

// Labeled horizontal bar (used in the cost-by-agent breakdown).
export function BarRow({ label, sub, value, max, display, color = "bg-brand-500" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      {/* min-w-0 + truncate: an agent name or a tester's local-part longer than
          the 6rem column used to wrap to two lines, pushing this row's bar and
          value out of alignment with every other row in the chart. */}
      <div className="w-24 min-w-0 shrink-0">
        <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200" title={label}>{label}</p>
        {sub && <p className="truncate text-[0.6875rem] text-slate-400">{sub}</p>}
      </div>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
        <div
          className={cn("h-full rounded-full transition-all", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="w-16 shrink-0 text-right text-sm font-semibold text-slate-800 dark:text-white">
        {display}
      </div>
    </div>
  );
}

// Why every bar chart below sets an explicit plot height
// ------------------------------------------------------
// These charts used to wrap their columns in `flex h-32 items-end`. `items-end`
// makes each column align to the bottom AND size to its own content, so a column
// was only as tall as its axis label. The bar track inside then asked for
// `flex-1` of a container with no spare height, resolved to 0px, and every
// percentage bar inside it computed to 0 — the charts rendered as a bare row of
// day letters with nothing above them.
//
// So: the plot area owns a definite height, the columns STRETCH into it (the
// flex default), and only the bars themselves use `items-end`. A percentage
// height then has a real containing block to resolve against.

// Small vertical bar chart (runs per day).
export function MiniBars({ data, labels, height = 128 }) {
  const max = Math.max(...data, 1);
  return (
    <div>
      <div className="flex gap-2" style={{ height }}>
        {data.map((v, i) => (
          <div key={i} className="flex flex-1 items-end">
            <div
              // A zero day keeps a 2px stub so the column still reads as "a day
              // with no runs" rather than as missing data.
              className={cn(
                "w-full rounded-t-md transition-all",
                v > 0 ? "bg-gradient-to-t from-brand-500 to-brand-400" : "bg-slate-100 dark:bg-white/10"
              )}
              style={{ height: v > 0 ? `${Math.max((v / max) * 100, 3)}%` : 2 }}
              title={`${v} run${v === 1 ? "" : "s"}`}
            />
          </div>
        ))}
      </div>
      {labels && (
        <div className="mt-1.5 flex gap-2">
          {labels.map((l, i) => (
            <span key={i} className="flex-1 text-center text-[0.625rem] text-slate-400">{l}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact per-day bars for a flow card.
//
// Replaces the sparkline that used to sit here: a line drawn through 7 daily
// counts implies a continuous trend, and with most days at zero it rendered as a
// flat rule with one spike — which read as a broken chart rather than as "one
// run, on Monday". Discrete days deserve discrete bars.
// `format` renders the tooltip value. It defaults to runs because that is what
// most callers plot, but the spend card passes money — without this its bars read
// "0.05 runs".
export function TrendBars({ data, titles, height = 30, color = "bg-brand-500", format }) {
  const max = Math.max(...data, 1);
  // A 30- or 90-day window puts far more bars in the same width, so the gap has
  // to shrink with it or the bars themselves round down to nothing.
  const dense = data.length > 14;
  return (
    <div className={cn("flex items-end", dense ? "gap-px" : "gap-[3px]")} style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className={cn(
            "min-w-0 flex-1 transition-all",
            dense ? "rounded-none" : "rounded-sm",
            v > 0 ? color : "bg-slate-100 dark:bg-white/10"
          )}
          style={{ height: v > 0 ? `${Math.max((v / max) * 100, 10)}%` : 2 }}
          title={`${titles?.[i] ? `${titles[i]}: ` : ""}${format ? format(v) : `${v} run${v === 1 ? "" : "s"}`}`}
        />
      ))}
    </div>
  );
}

// Vertical GROUPED bar chart — one bar per series, per day.
// `labels` = x-axis labels; `series` = [{ key, label, color, data:[per-day] }].
//
// Grouped rather than stacked: with only two live flows a stack buries the
// smaller one inside the column, and the question this chart answers is which
// flow ran how much on a given day. Side-by-side bars answer it directly.
// `format` renders tick/legend/tooltip values. Defaults to whole "N runs" —
// the original shape every caller here still uses. A caller plotting money
// (which genuinely has fractional values, unlike a run count) passes its own
// formatter, e.g. `(v) => formatMoney(v)`.
export function GroupedBars({ labels, titles, series, height = 160, empty = "No runs in this period.", format }) {
  const values = series.flatMap((ser) => ser.data || []);
  // The axis top is the real peak; 1 is only a floor for a series with nothing
  // positive in it (which renders the empty state below anyway, so it is just a
  // safe divisor).
  //
  // It used to be `Math.max(1, ...values)` unconditionally. For a RUN COUNT that
  // is a no-op — any non-empty count series already peaks at 1 or more — but for
  // MONEY it pinned the axis to $1.00 whatever the numbers were, so a chart of a
  // platform that had spent ten cents drew every bar as a two-pixel sliver under
  // three gridlines it would never reach.
  const peak = Math.max(0, ...values);
  const max = peak > 0 ? peak : 1;
  const grandTotal = values.reduce((sum, n) => sum + (n || 0), 0);
  const fmt = format || ((v) => `${v}`);
  // Whole runs only — a "2.5 runs" gridline labels something that cannot
  // happen. That rounding is specific to counts, so skip it for a caller with
  // its own (money-aware) formatter.
  const half = format ? max / 2 : Math.round(max / 2);
  const ticks = [max, half, 0].filter((t, i, a) => a.indexOf(t) === i);
  // Past ~2 weeks the per-day gaps eat more width than the bars do.
  const dense = labels.length > 14;

  return (
    <div>
      {grandTotal === 0 ? (
        // An all-zero week used to render as an axis with no bars, which looks
        // identical to a chart that failed to load. Say which one it is.
        <div
          className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 dark:border-white/10"
          style={{ height }}
        >
          {empty}
        </div>
      ) : (
        <div className="flex gap-3">
          <div
            className="flex shrink-0 flex-col justify-between text-right text-[0.625rem] leading-none tabular-nums text-slate-400"
            style={{ height }}
          >
            {ticks.map((t) => <span key={t}>{fmt(t)}</span>)}
          </div>

          <div className="min-w-0 flex-1">
            <div className="relative" style={{ height }}>
              <div className="absolute inset-0 flex flex-col justify-between" aria-hidden="true">
                {ticks.map((t) => (
                  <span key={t} className="block border-t border-slate-100 dark:border-white/5" />
                ))}
              </div>
              <div className={cn("relative flex h-full", dense ? "gap-px" : "gap-2")}>
                {labels.map((label, i) => (
                  <div key={i} className={cn("flex min-w-0 flex-1 items-end justify-center", dense ? "gap-0" : "gap-1")}>
                    {series.map((ser) => {
                      const v = (ser.data || [])[i] || 0;
                      return (
                        <div
                          key={ser.key}
                          className={cn(
                            "min-w-0 flex-1 transition-all",
                            dense ? "rounded-none" : "rounded-t",
                            v > 0 ? ser.color : "bg-slate-100 dark:bg-white/10"
                          )}
                          style={{ height: v > 0 ? `${Math.max((v / max) * 100, 4)}%` : 2 }}
                          // `labels` goes sparse on a wide window, so the tooltip
                          // reads the always-present title instead of a blank tick.
                          title={`${ser.label} · ${titles?.[i] || label}: ${format ? fmt(v) : `${v} run${v === 1 ? "" : "s"}`}`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className={cn("mt-1.5 flex", dense ? "gap-px" : "gap-2")}>
              {labels.map((label, i) => (
                <span key={i} className="min-w-0 flex-1 whitespace-nowrap text-center text-[0.625rem] text-slate-400">
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {series.map((ser) => (
          <span key={ser.key} className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className={cn("h-2.5 w-2.5 rounded-sm", ser.color)} />
            {ser.label}{" "}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {fmt((ser.data || []).reduce((sum, n) => sum + (n || 0), 0))}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Minimal area sparkline. The fill gradient id is derived from the stroke color
// so several sparklines of DIFFERENT colors can coexist on one page without the
// duplicate-id collision that would make them all share the first gradient.
export function Sparkline({ data, width = 220, height = 56, stroke = "#6366f1" }) {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  // A single-point series makes the divisor 0 → Infinity → NaN coordinates and a
  // silently blank <svg>. Guard rather than rely on every caller checking length.
  const step = data.length > 1 ? width / (data.length - 1) : width;
  const points = data.map((v, i) => [
    i * step,
    height - ((v - min) / range) * (height - 6) - 3,
  ]);
  const line = points.map((p) => `${p[0]},${p[1]}`).join(" ");
  const area = `0,${height} ${line} ${width},${height}`;
  const gradId = `spark-${String(stroke).replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.3" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
